import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * @param {CartAddEvent} event
   */
  #handleCartAdd = async (event) => {
    if (this.hasAttribute('auto-open')) {
      const dialog = this.refs.dialog;
      const wasEmpty = dialog?.classList.contains('cart-drawer--empty');
      
      if (wasEmpty) {
        // Cart was empty, need to fetch fresh cart drawer HTML
        try {
          // Get the header section ID
          const headerSection = this.closest('[id^="shopify-section-"]');
          const sectionId = headerSection?.id.replace('shopify-section-', '') || 'header';
          
          // Fetch fresh section HTML
          const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`, {
            method: 'GET',
            headers: {
              'Accept': 'text/html',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          if (response.ok) {
            const html = await response.text();
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            // Find the new cart drawer in the fetched HTML
            const newCartDrawer = tempDiv.querySelector('cart-drawer-component');
            if (newCartDrawer) {
              // Replace the entire cart drawer with the fresh one
              const parent = this.parentElement;
              if (parent) {
                parent.replaceChild(newCartDrawer, this);
                
                // Wait for the new drawer to be ready and open it
                setTimeout(() => {
                  const updatedDrawer = parent.querySelector('cart-drawer-component');
                  if (updatedDrawer && 'showDialog' in updatedDrawer) {
                    /** @type {CartDrawerComponent} */ (updatedDrawer).showDialog();
                  }
                }, 100);
              }
              return;
            }
          } else {
            // If fetch failed, at least remove the empty class and force update cart items
            dialog.classList.remove('cart-drawer--empty');
            this.#forceUpdateCartItems();
            this.showDialog();
          }
        } catch (error) {
          console.error('Failed to update cart drawer:', error);
          // Fallback: remove empty class and force update
          dialog.classList.remove('cart-drawer--empty');
          this.#forceUpdateCartItems();
          this.showDialog();
        }
      } else {
        // Default behavior for non-empty cart
        this.showDialog();
      }
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Force update cart items component
   */
  #forceUpdateCartItems() {
    const cartItemsComponent = this.querySelector('cart-items-component');
    if (cartItemsComponent && 'sectionId' in cartItemsComponent) {
      // Dispatch a custom event to force re-render
      window.dispatchEvent(new CustomEvent('cart:force-update', {
        detail: { sectionId: cartItemsComponent.sectionId }
      }));
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
